"use client";
import { useState, useEffect } from "react";
import { API_BASE_URL } from "../../utils/apiConfig";
import { exportTransactionsToExcel } from "../../utils/excelExport";
import {
  DollarSign,
  CreditCard,
  List,
  ChevronLeft,
  ChevronRight,
  Eye,
  ExternalLink,
  Calendar,
  Wallet,
  Hash,
  Filter,
  RotateCcw,
  Download,
  Copy,
  Check,
} from "lucide-react";

const Button = ({
  onClick,
  children,
  variant = "primary",
  disabled = false,
  icon: Icon,
  className = "",
}) => {
  const baseClasses =
    "flex items-center justify-center space-x-2 px-4 py-2.5 sm:px-6 sm:py-1.5 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base";
  const variants = {
    primary:
      "bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-purple-500/25 transform hover:scale-[1.02]",
    success:
      "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg hover:shadow-green-500/25 transform hover:scale-[1.02]",
    danger:
      "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg hover:shadow-red-500/25 transform hover:scale-[1.02]",
    outline:
      "border border-slate-300 bg-white/80 backdrop-blur-sm text-slate-700 hover:bg-slate-50 hover:border-slate-400 shadow-sm",
    secondary:
      "bg-slate-200 hover:bg-slate-300 text-slate-700 shadow-sm transform hover:scale-[1.02]",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${className}`}
    >
      {Icon && <Icon className="w-2 h-2 sm:w-5 sm:h-5" />}
      <span className="inline">{children}</span>
    </button>
  );
};

const AllTransactions = () => {
  const [allTransactions, setAllTransactions] = useState([]); // Store all fetched transactions for current tab
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [displayedTransactions, setDisplayedTransactions] = useState([]); // Transactions to show on current page

  // No more static sample data - we'll show real data from API

  const [selectedTab, setSelectedTab] = useState("deposit");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [backButtonVisible, setBackButtonVisible] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [receiptLinkCopied, setReceiptLinkCopied] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [totalTransactions, setTotalTransactions] = useState(0);

  // Filter states
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false); // Hidden by default
  const [dateSort, setDateSort] = useState("newest"); // newest, oldest, updated
  const [searchTerm, setSearchTerm] = useState("");

  // Get unique statuses dynamically from predefined options based on transaction type
  const getUniqueStatuses = () => {
    if (selectedTab === "deposit") {
      return [
        "initiated",
        "confirmed",
        "failed",
        "expired",
        "pending",
        "api_failed",
      ];
    } else if (selectedTab === "payout") {
      return ["pending", "rejected", "completed", "failed"];
    } else if (selectedTab === "withdrawal") {
      return ["pending", "rejected", "completed", "failed"];
    }
    return [];
  };

  // Format time ago for display
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInHours / 24;

    if (diffInHours < 1) {
      const diffInMinutes = diffInMs / (1000 * 60);
      return `${Math.floor(diffInMinutes)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      dateFilter.startDate || dateFilter.endDate || statusFilter || searchTerm
    );
  };

  // Apply filters by calling API with filter params
  const applyFilters = () => {
    setCurrentPage(1);
    fetchTransactions(1, dateFilter, statusFilter);
  };

  // Clear all filters and refetch
  const clearFilters = () => {
    setDateFilter({ startDate: "", endDate: "" });
    setStatusFilter("");
    setSearchTerm("");
    setIsFiltering(false);
    setCurrentPage(1);
    fetchTransactions(1, { startDate: "", endDate: "" }, "");
  };

  // Fetch transactions for current page, with optional filters
  const fetchTransactions = async (
    page = 1,
    dateFilterArg = dateFilter,
    statusFilterArg = statusFilter,
  ) => {
    setLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      params.append("type", selectedTab);
      params.append("limit", limit);
      params.append("page", page);
      if (dateFilterArg.startDate)
        params.append("startDate", dateFilterArg.startDate);
      if (dateFilterArg.endDate)
        params.append("endDate", dateFilterArg.endDate);
      if (statusFilterArg) params.append("status", statusFilterArg);
      if (dateSort) params.append("sort", dateSort);
      params.append("filter", "range");

      const response = await fetch(
        `${API_BASE_URL}/dashboard/transactionList?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const dashboardData = data.data;
        // Extract pagination info from API response
        const apiPagination = {
          currentPage: dashboardData.page || 1,
          limit: dashboardData.limit || 10,
          total: dashboardData.total || 0,
          totalPages: dashboardData.totalPages || 1,
        };

        // Ensure dashboardData.docs is an array
        let transactionsArray = [];
        if (Array.isArray(dashboardData.docs)) {
          transactionsArray = dashboardData.docs;
        }

        let recentTransactions = transactionsArray;

        if (selectedTab === "deposit") {
          recentTransactions = transactionsArray.filter(
            (tx) =>
              tx.type === "deposit" ||
              tx.type === "topup" ||
              tx.verificationData ||
              tx.walletAddress ||
              tx.transactionId ||
              (!tx.type && tx.amount),
          );
        } else if (selectedTab === "payout") {
          recentTransactions = transactionsArray.filter(
            (tx) =>
              tx.type === "withdrawal" ||
              tx.type === "payout" ||
              tx.type === "withdraw" ||
              tx.type === "refund" ||
              tx.blockchainData ||
              tx.clientWallet ||
              (!tx.type && tx.status === "withdrawn"),
          );
        } else if (selectedTab === "withdrawal") {
          // Include type="refund" — these are mislabeled withdrawal requests
          // (old frontend bug, fixed in commit 5b53c70)
          recentTransactions = transactionsArray.filter(
            (tx) => tx.type === "withdrawal" || tx.type === "withdraw" || tx.type === "refund",
          );
        }

        const formattedTransactions = recentTransactions.map((tx, index) => {
          const amount = Number(tx.amount) || 0;
          const actualBalance = Number(tx.actualBalance) || 0;
          let paymentStatus = "";

          if (selectedTab === "deposit" && actualBalance !== undefined) {
            if (
              tx.status === "confirmed" ||
              tx.status === "completed" ||
              tx.status === "approved"
            ) {
              paymentStatus = "Exact Paid";
              if (amount > 0) {
                if (actualBalance > amount) {
                  paymentStatus = "Overpaid";
                } else if (actualBalance < amount) {
                  paymentStatus = "Underpaid";
                } else {
                  paymentStatus = "Exact Paid";
                }
              }
            }
          }

          return {
            ...tx,
            type: selectedTab,
            _id: `${selectedTab}_${tx.wallet || tx.clientWallet}_${
              tx.amount || "no-amount"
            }_${tx.email}_${page}_${index}`,
            createdAt: tx.createdAt || new Date().toISOString(),
            displayAmount: amount,
            actualBalance: actualBalance,
            paymentStatus: paymentStatus,
          };
        });

        // Set transactions and pagination data from API response
        setAllTransactions(formattedTransactions);
        setFilteredTransactions(formattedTransactions);
        setDisplayedTransactions(formattedTransactions);
        setTotalTransactions(apiPagination.total);
        setTotalPages(apiPagination.totalPages);
        setHasNextPage(apiPagination.currentPage < apiPagination.totalPages);

        // Update current page if it differs from what we requested
        if (apiPagination.currentPage !== page) {
          setCurrentPage(apiPagination.currentPage);
        }
      } else {
        setAllTransactions([]);
        setFilteredTransactions([]);
        setDisplayedTransactions([]);
        setHasNextPage(false);
        setTotalTransactions(0);
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);

      // Provide more user-friendly error messages
      let errorMessage = "Failed to fetch transactions";
      if (err.message.includes("401")) {
        errorMessage = "Authentication required. Please log in again.";
      } else if (err.message.includes("403")) {
        errorMessage =
          "Access denied. You don't have permission to view transactions.";
      } else if (err.message.includes("404")) {
        errorMessage =
          "Transactions endpoint not found. Please contact support.";
      } else if (err.message.includes("500")) {
        errorMessage = "Server error. Please try again later.";
      } else if (
        err.message.includes("NetworkError") ||
        err.message.includes("fetch")
      ) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      setError(errorMessage);
      setAllTransactions([]);
      setFilteredTransactions([]);
      setDisplayedTransactions([]);
      setHasNextPage(false);
      setTotalTransactions(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle page changes - simplified for server-side pagination
  const handlePageChange = (direction) => {
    const newPage = Math.max(1, currentPage + direction);

    // For server-side pagination, always fetch from API
    if (direction === 1) {
      // Going to next page
      if (hasNextPage && newPage <= totalPages) {
        setCurrentPage(newPage);
        fetchTransactions(newPage);
      }
    } else {
      // Going to previous page
      if (newPage >= 1) {
        setCurrentPage(newPage);
        fetchTransactions(newPage);
      }
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setAllTransactions([]);
    setFilteredTransactions([]);
    setDisplayedTransactions([]);
    fetchTransactions(1);
  }, [selectedTab]);

  useEffect(() => {
    const pathname =
      typeof window !== "undefined" ? window.location.pathname : "";
    if (pathname === "/admin") {
      setBackButtonVisible(false);
    } else {
      setBackButtonVisible(true);
    }
  }, []);

  // Apply filters whenever filter criteria change
  useEffect(() => {
    if (hasActiveFilters()) {
      setIsFiltering(true);
      applyFilters();
    } else if (isFiltering) {
      // Only clear if we were previously filtering
      clearFilters();
    }
  }, [dateFilter, statusFilter]);

  // Apply date sorting whenever dateSort changes - now handled by server
  useEffect(() => {
    // Reset to page 1 and refetch with new sorting
    setCurrentPage(1);
    fetchTransactions(1, dateFilter, statusFilter);
  }, [dateSort]);

  // Apply search filtering on current transactions
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setDisplayedTransactions(allTransactions);
    } else {
      const filtered = allTransactions.filter((transaction) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (transaction.transactionId || transaction.id || "")
            .toLowerCase()
            .includes(searchLower) ||
          (transaction.username || "").toLowerCase().includes(searchLower) ||
          (transaction.firstName || "").toLowerCase().includes(searchLower) ||
          (transaction.lastName || "").toLowerCase().includes(searchLower) ||
          (transaction.email || "").toLowerCase().includes(searchLower) ||
          (transaction.phoneNumber || "").toLowerCase().includes(searchLower) ||
          (transaction.referenceId || "").toLowerCase().includes(searchLower) ||
          (transaction.description || "").toLowerCase().includes(searchLower) ||
          (transaction.walletAddress || transaction.clientWallet || "")
            .toLowerCase()
            .includes(searchLower) ||
          (transaction.marchantUserId || "")
            .toLowerCase()
            .includes(searchLower) ||
          (transaction.invoiceNo || "").toLowerCase().includes(searchLower) ||
          (transaction.transactionHash || transaction.blockchainTxHash || "")
            .toLowerCase()
            .includes(searchLower) ||
          (transaction.adminNotes || "").toLowerCase().includes(searchLower) ||
          (transaction.currencyType || "")
            .toLowerCase()
            .includes(searchLower) ||
          transaction.amount.toString().includes(searchTerm) ||
          transaction.status.toLowerCase().includes(searchLower) ||
          transaction.type.toLowerCase().includes(searchLower)
        );
      });
      setDisplayedTransactions(filtered);
    }
  }, [searchTerm, allTransactions]);

  const handleBackClick = () => {
    if (typeof window !== "undefined") {
      window.history.back();
    }
  };

  // Export current tab transactions to Excel (fetch all pages)
  const handleExportCurrentTab = async () => {
    if (
      !window.confirm(
        `This will export ALL ${selectedTab} transactions from all pages. Continue?`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);

      const filters = {
        status: statusFilter,
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
        sort: dateSort,
      };

      // Fetch all pages of data for current tab
      let allData = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const params = new URLSearchParams();
        params.append("type", selectedTab);
        params.append("limit", "100"); // Get 100 records per page
        params.append("page", currentPage);
        if (filters.startDate) params.append("startDate", filters.startDate);
        if (filters.endDate) params.append("endDate", filters.endDate);
        if (filters.status) params.append("status", filters.status);
        if (filters.sort) params.append("sort", filters.sort);
        params.append("filter", "range");

        const response = await fetch(
          `${API_BASE_URL}/dashboard/transactionList?${params.toString()}`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch ${selectedTab} transactions`);
        }

        const data = await response.json();

        if (data.success && data.data.docs && data.data.docs.length > 0) {
          // Format transactions similar to how it's done in fetchTransactions
          const formattedTransactions = data.data.docs.map((tx, index) => {
            const amount = parseFloat(tx.amount || 0);
            const actualBalance = parseFloat(tx.actualBalance || 0);

            let paymentStatus = "";
            if (selectedTab === "deposit" && tx.actualBalance !== undefined) {
              if (
                tx.status === "confirmed" ||
                tx.status === "completed" ||
                tx.status === "approved"
              ) {
                if (actualBalance < amount) {
                  paymentStatus = "Underpaid";
                } else if (actualBalance > amount) {
                  paymentStatus = "Overpaid";
                } else {
                  paymentStatus = "Exact";
                }
              }
            }

            return {
              ...tx,
              type: selectedTab,
              _id: `${selectedTab}_${tx.wallet || tx.clientWallet}_${
                tx.amount || "no-amount"
              }_${tx.email}_${currentPage}_${index}`,
              createdAt: tx.createdAt || new Date().toISOString(),
              displayAmount: amount,
              actualBalance: actualBalance,
              paymentStatus: paymentStatus,
            };
          });

          allData = [...allData, ...formattedTransactions];

          // Check if there are more pages
          hasMorePages = currentPage < (data.data.totalPages || 1);
          currentPage++;
        } else {
          hasMorePages = false;
        }
      }

      if (allData.length === 0) {
        alert(`No ${selectedTab} transactions found to export`);
        return;
      }

      const result = exportTransactionsToExcel(allData, selectedTab, filters);

      if (result.success) {
        alert(result.message);
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error("Export error:", error);
      alert(`Failed to export ${selectedTab} transactions. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type, status) => {
    if (type === "deposit") {
      if (
        status === "completed" ||
        status === "approved" ||
        status === "confirmed"
      ) {
        return (
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center shadow-sm">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
          </div>
        );
      }
      return (
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center shadow-sm">
          <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
        </div>
      );
    }

    if (type === "payout") {
      if (
        status === "completed" ||
        status === "approved" ||
        status === "confirmed"
      ) {
        return (
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center shadow-sm">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
          </div>
        );
      }
      return (
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center shadow-sm">
          <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
        </div>
      );
    }

    if (type === "withdrawal" || type === "withdraw" || type === "refund") {
      if (
        status === "completed" ||
        status === "approved" ||
        status === "confirmed"
      ) {
        return (
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center shadow-sm">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
          </div>
        );
      }
      return (
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center shadow-sm">
          <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
        </div>
      );
    }

    return (
      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center shadow-sm">
        <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-300";
      case "approved":
      case "confirmed":
        return "bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-300";
      case "pending":
      case "processing":
        return "bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border-yellow-300";
      case "failed":
      case "blocked":
      case "cancelled":
        return "bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300";
      case "initiated":
        return "bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border-orange-300";
      case "expired":
        return "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border-gray-300";
      case "rejected":
        return "bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300";
      default:
        return "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border-gray-300";
    }
  };

  const getAmountColor = (transaction) => {
    // For expired transactions, show gray
    if (transaction.status === "expired") {
      return "text-gray-600";
    }

    if (transaction.type === "deposit") {
      if (transaction.status === "initiated") {
        return "text-orange-600";
      }
      if (transaction.paymentStatus === "Underpaid") {
        return "text-red-600";
      }
      return "text-green-600";
    }
    if (transaction.type === "withdrawal" || transaction.type === "withdraw" || transaction.type === "refund") {
      return "text-blue-600";
    }
    return "text-red-600";
  };

  const getUnderpaidStatusColor = (transaction) => {
    if (transaction.paymentStatus === "Underpaid") {
      return "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border-gray-300";
    }
    return getStatusColor(transaction.status);
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {backButtonVisible && (
            <Button
              onClick={handleBackClick}
              variant="outline"
              icon={ChevronLeft}
              className="w-fit bg-transparent"
            >
              Back
            </Button>
          )}
          <div className="text-center sm:text-right">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              All Transactions
            </h1>
            <p className="text-slate-600 text-sm sm:text-base">
              Manage your deposits, payouts, and withdrawals
            </p>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-slate-200/50 p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex space-x-2 sm:space-x-4">
              <button
                onClick={() => setSelectedTab("deposit")}
                className={`flex-1 sm:flex-none py-3 px-4 sm:px-6 rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${
                  selectedTab === "deposit"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-[1.02]"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                <span className="hidden sm:inline">💰 </span>Deposits
              </button>
              <button
                onClick={() => setSelectedTab("payout")}
                className={`flex-1 sm:flex-none py-3 px-4 sm:px-6 rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${
                  selectedTab === "payout"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-[1.02]"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                <span className="hidden sm:inline">💸 </span>Payouts
              </button>
              <button
                onClick={() => setSelectedTab("withdrawal")}
                className={`flex-1 sm:flex-none py-3 px-4 sm:px-6 rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${
                  selectedTab === "withdrawal"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-[1.02]"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                <span className="hidden sm:inline">🔄 </span>Withdraw
              </button>
            </div>
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              {/* Export Current Tab Button */}
              <Button
                onClick={handleExportCurrentTab}
                variant="success"
                icon={Download}
                className="w-fit"
                disabled={loading || displayedTransactions.length === 0}
              >
                Export {selectedTab}
              </Button>
            </div>
          </div>

          {/* Search Bar - Always Visible */}
          <div className="bg-slate-50/80 rounded-xl p-4 sm:p-6 mb-6 border border-slate-200">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex-1">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  🔍 Search Transactions
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by transaction ID, email, username, reference, description, wallet address..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                />
              </div>
              <div className="flex gap-2">
                {/* Filter Toggle Button */}
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant={showFilters ? "primary" : "outline"}
                  icon={Filter}
                  className="w-fit"
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </Button>
                {/* Clear All Button */}
                {hasActiveFilters() && (
                  <Button
                    onClick={clearFilters}
                    variant="outline"
                    icon={RotateCcw}
                    className="w-fit bg-transparent"
                  >
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div className="bg-slate-50/80 rounded-xl p-4 sm:p-6 mb-6 border border-slate-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Date Filters */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    <Calendar className="inline w-4 h-4 mr-1" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={dateFilter.startDate}
                    onChange={(e) =>
                      setDateFilter((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    <Calendar className="inline w-4 h-4 mr-1" />
                    End Date
                  </label>
                  <input
                    type="date"
                    value={dateFilter.endDate}
                    onChange={(e) =>
                      setDateFilter((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  />
                </div>
                {/* Status Filter */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="">All Statuses</option>
                    {getUniqueStatuses().map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Sort Filter */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Sort By Date
                  </label>
                  <select
                    value={dateSort}
                    onChange={(e) => setDateSort(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="updated">Recently Updated</option>
                  </select>
                </div>
              </div>
              {/* Filter Summary */}
              {hasActiveFilters() && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium">
                    Active Filters:
                    {searchTerm && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 rounded text-xs">
                        Search: "{searchTerm}"
                      </span>
                    )}
                    {dateFilter.startDate && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 rounded text-xs">
                        From:{" "}
                        {new Date(dateFilter.startDate).toLocaleDateString()}
                      </span>
                    )}
                    {dateFilter.endDate && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 rounded text-xs">
                        To: {new Date(dateFilter.endDate).toLocaleDateString()}
                      </span>
                    )}
                    {statusFilter && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 rounded text-xs">
                        Status:{" "}
                        {statusFilter.charAt(0).toUpperCase() +
                          statusFilter.slice(1)}
                      </span>
                    )}
                    <span className="ml-2 px-2 py-1 bg-blue-100 rounded text-xs">
                      Sort:{" "}
                      {dateSort === "newest"
                        ? "Newest First"
                        : dateSort === "oldest"
                          ? "Oldest First"
                          : "Recently Updated"}
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-gradient-to-r from-red-100 to-red-200 border border-red-300 text-red-800 px-4 py-3 rounded-xl mb-6 shadow-sm">
              <div className="flex items-center space-x-2">
                <span className="text-red-600">⚠️</span>
                <span className="font-medium">Error: {error}</span>
              </div>
            </div>
          )}

          {/* Transactions List */}
          <div>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800">
                {selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}{" "}
                Transactions
                {hasActiveFilters() && (
                  <span className="text-sm font-normal text-blue-600 ml-2">
                    ({totalTransactions} total results)
                  </span>
                )}
                {displayedTransactions.length === 0 &&
                  allTransactions.length === 0 && (
                    <span className="text-sm font-normal text-blue-600 ml-2">
                      (no transactions available)
                    </span>
                  )}
              </h2>
              <div className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                Page {currentPage} of {totalPages}
              </div>
            </div>

            {loading && allTransactions.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-3 border-blue-500 mx-auto"></div>
                <p className="text-slate-500 mt-4 text-sm sm:text-base">
                  Loading transactions...
                </p>
              </div>
            ) : loading && allTransactions.length > 0 ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-slate-500 mt-2 text-sm">Refreshing...</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {displayedTransactions.length === 0 ? (
                  // Show empty state when no transactions are available
                  <div className="text-center py-12 sm:py-16">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <List className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-base sm:text-lg font-medium">
                      {allTransactions.length === 0
                        ? `No ${selectedTab} transactions found`
                        : "No transactions match your filters"}
                    </p>
                    <p className="text-slate-400 text-sm sm:text-base mt-2">
                      {allTransactions.length === 0
                        ? "Your transactions will appear here once available"
                        : "Try adjusting your filter criteria"}
                    </p>
                    {/* No refresh button needed */}
                  </div>
                ) : (
                  displayedTransactions.map((transaction) => (
                    <div
                      key={transaction._id}
                      className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white hover:shadow-lg hover:scale-[1.01] cursor-pointer transition-all duration-300"
                      onClick={() => handleTransactionClick(transaction)}
                    >
                      {/* Mobile Layout */}
                      <div className="block sm:hidden p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {getTransactionIcon(
                              transaction.type,
                              transaction.status,
                            )}
                            <div>
                              <div className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                                {transaction.type === "deposit"
                                  ? "Deposit"
                                  : transaction.type === "payout"
                                    ? "Payout"
                                    : "Withdrawal"}

                                {/* New Badge for recent transactions (within last 24 hours) */}
                                {(() => {
                                  const transactionDate = new Date(
                                    transaction.createdAt ||
                                      transaction.submittedAt ||
                                      0,
                                  );
                                  const now = new Date();
                                  const hoursDiff =
                                    (now - transactionDate) / (1000 * 60 * 60);
                                  return hoursDiff < 24 ? (
                                    <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                      NEW
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                              <div className="text-xs text-slate-500">
                                {transaction.walletAddress ||
                                transaction.clientWallet
                                  ? `${(
                                      transaction.walletAddress ||
                                      transaction.clientWallet
                                    ).substring(0, 8)}...`
                                  : "N/A"}
                              </div>
                            </div>
                          </div>
                          <Eye size={16} className="text-blue-600" />
                        </div>
                        <div className="flex justify-between items-center">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-lg ${getUnderpaidStatusColor(
                              transaction,
                            )}`}
                          >
                            {transaction.status.toUpperCase()}
                          </span>
                          <div className="text-right">
                            <div
                              className={`font-bold text-sm ${getAmountColor(
                                transaction,
                              )}`}
                            >
                              $
                              {transaction.type === "deposit" &&
                              transaction.actualBalance !== undefined &&
                              transaction.status === "confirmed"
                                ? transaction.actualBalance
                                : transaction.displayAmount || "0.00"}
                            </div>
                            {transaction.type === "deposit" &&
                              transaction.actualBalance !== undefined &&
                              transaction.status === "confirmed" && (
                                <div className="text-xs text-blue-600">
                                  Requested: $
                                  {transaction.displayAmount || "0.00"}
                                </div>
                              )}
                          </div>
                        </div>
                        {transaction.type === "deposit" &&
                          transaction.paymentStatus && (
                            <div className="text-center">
                              <span
                                className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                  transaction.paymentStatus === "Overpaid"
                                    ? "bg-green-100 text-green-700"
                                    : transaction.paymentStatus === "Underpaid"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-blue-100 text-blue-700"
                                }`}
                              >
                                {transaction.paymentStatus}
                              </span>
                            </div>
                          )}

                        {/* Date display for mobile */}
                        <div className="text-center text-xs text-slate-500">
                          {transaction.createdAt
                            ? formatTimeAgo(transaction.createdAt)
                            : "N/A"}
                        </div>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden sm:flex items-center justify-between p-4 sm:p-6">
                        <div className="flex items-center space-x-4">
                          {getTransactionIcon(
                            transaction.type,
                            transaction.status,
                          )}
                          <div className="space-y-1">
                            <div className="font-semibold text-slate-800 flex items-center gap-2">
                              {transaction.type === "deposit"
                                ? "Deposit"
                                : transaction.type === "payout"
                                  ? "Payout"
                                  : "Withdrawal"}
                              {(transaction.status === "failed" ||
                                transaction.status === "rejected") &&
                                ` (${transaction.status})`}

                              {/* New Badge for recent transactions (within last 24 hours) */}
                              {(() => {
                                const transactionDate = new Date(
                                  transaction.createdAt ||
                                    transaction.submittedAt ||
                                    0,
                                );
                                const now = new Date();
                                const hoursDiff =
                                  (now - transactionDate) / (1000 * 60 * 60);
                                return hoursDiff < 24 ? (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                    NEW
                                  </span>
                                ) : null;
                              })()}
                            </div>
                            <div className="text-slate-500 text-sm">
                              {transaction.walletAddress ||
                              transaction.clientWallet
                                ? `${(
                                    transaction.walletAddress ||
                                    transaction.clientWallet
                                  ).substring(0, 12)}...`
                                : "N/A"}
                            </div>
                            <div className="flex items-center space-x-4 text-xs text-slate-400">
                              <span>Status: {transaction.status}</span>
                              <span>•</span>
                              <span>
                                {transaction.createdAt
                                  ? formatTimeAgo(transaction.createdAt)
                                  : "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right space-y-2">
                            <div
                              className={`font-bold text-lg ${getAmountColor(
                                transaction,
                              )}`}
                            >
                              $
                              {transaction.type === "deposit" &&
                              transaction.actualBalance !== undefined &&
                              transaction.status === "confirmed"
                                ? transaction.actualBalance
                                : transaction.displayAmount || "0.00"}
                            </div>
                            {transaction.type === "deposit" &&
                              transaction.actualBalance !== undefined &&
                              transaction.status === "confirmed" && (
                                <div className="text-sm text-blue-600">
                                  Requested: $
                                  {transaction.displayAmount || "0.00"}
                                </div>
                              )}
                            {transaction.type === "deposit" &&
                              transaction.paymentStatus &&
                              transaction.status === "confirmed" && (
                                <div className="text-sm">
                                  <span
                                    className={`font-semibold ${
                                      transaction.paymentStatus === "Overpaid"
                                        ? "text-green-700"
                                        : transaction.paymentStatus ===
                                            "Underpaid"
                                          ? "text-red-700"
                                          : "text-blue-700"
                                    }`}
                                  >
                                    {transaction.paymentStatus}
                                  </span>
                                </div>
                              )}
                            {/* {(transaction.type === "payout" || transaction.type === "refund") &&
                              transaction.netAmount && (
                                <div className="text-sm text-red-600">
                                  Received: {transaction.netAmount.toFixed(3)} USDT
                                </div>
                              )} */}
                            <span
                              className={`inline-block px-3 py-1 text-xs font-medium rounded-lg ${getUnderpaidStatusColor(
                                transaction,
                              )}`}
                            >
                              {transaction.status.toUpperCase()}
                            </span>
                          </div>
                          <Eye
                            size={20}
                            className="text-blue-600 hover:text-blue-800"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t border-slate-200">
            <Button
              onClick={() => handlePageChange(-1)}
              disabled={currentPage === 1}
              variant={currentPage === 1 ? "secondary" : "primary"}
              icon={ChevronLeft}
              className="group"
            >
              <span className="hidden sm:inline">
                Previous{" "}
                {selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}
              </span>
              <span className="sm:hidden">Previous</span>
            </Button>
            <div className="flex items-center space-x-3 px-4 py-2 bg-slate-100 rounded-xl border border-slate-200">
              <div className="flex items-center space-x-2 text-slate-700">
                <span className="text-sm sm:text-base font-bold">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
              {hasActiveFilters() && (
                <div className="hidden sm:flex items-center space-x-1">
                  <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                  <span className="text-xs text-blue-600 font-medium">
                    Filtered: {totalTransactions} total
                  </span>
                </div>
              )}
            </div>
            <Button
              onClick={() => handlePageChange(1)}
              disabled={!hasNextPage || currentPage >= totalPages}
              variant={
                !hasNextPage || currentPage >= totalPages
                  ? "secondary"
                  : "primary"
              }
              icon={ChevronRight}
              className="group"
            >
              <span className="hidden sm:inline">
                Next{" "}
                {selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}
              </span>
              <span className="sm:hidden">Next</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
                  Transaction Details
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Complete transaction information
                </p>
              </div>
              <Button
                onClick={() => setShowDetailsModal(false)}
                variant="primary"
              >
                Close Details
              </Button>
            </div>
            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6 sm:space-y-8">
                {/* Status Banner */}
                <div className="text-center">
                  <span
                    className={`inline-block px-4 py-2 text-sm font-bold rounded-xl ${getStatusColor(
                      selectedTransaction.status,
                    )}`}
                  >
                    {selectedTransaction.status.toUpperCase()}
                  </span>
                </div>
                {/* Customer Information (if available) */}
                {(selectedTransaction.username ||
                  selectedTransaction.firstName ||
                  selectedTransaction.email ||
                  selectedTransaction.phoneNumber ||
                  selectedTransaction.referenceId) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {selectedTransaction.username && (
                      <div className="bg-slate-50 rounded-xl p-4">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Username
                        </label>
                        <p className="text-sm font-medium text-slate-800 bg-white px-3 py-2 rounded-lg">
                          {selectedTransaction.username}
                        </p>
                      </div>
                    )}
                    {(selectedTransaction.firstName ||
                      selectedTransaction.lastName) && (
                      <div className="bg-slate-50 rounded-xl p-4">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Full Name
                        </label>
                        <p className="text-sm font-medium text-slate-800 bg-white px-3 py-2 rounded-lg">
                          {[
                            selectedTransaction.firstName,
                            selectedTransaction.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        </p>
                      </div>
                    )}
                    {selectedTransaction.email && (
                      <div className="bg-slate-50 rounded-xl p-4">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Email Address
                        </label>
                        <p className="text-sm font-medium text-slate-800 bg-white px-3 py-2 rounded-lg">
                          {selectedTransaction.email}
                        </p>
                      </div>
                    )}
                    {selectedTransaction.phoneNumber && (
                      <div className="bg-slate-50 rounded-xl p-4">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Phone Number
                        </label>
                        <p className="text-sm font-medium text-slate-800 bg-white px-3 py-2 rounded-lg">
                          {selectedTransaction.phoneNumber}
                        </p>
                      </div>
                    )}
                    {selectedTransaction.referenceId && (
                      <div className="bg-slate-50 rounded-xl p-4">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Reference ID
                        </label>
                        <p className="text-sm font-medium text-slate-800 bg-white px-3 py-2 rounded-lg">
                          {selectedTransaction.referenceId}
                        </p>
                      </div>
                    )}
                    {selectedTransaction.description && (
                      <div className="bg-slate-50 rounded-xl p-4 sm:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Description
                        </label>
                        <p className="text-sm font-medium text-slate-800 bg-white px-3 py-2 rounded-lg">
                          {selectedTransaction.description}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <Hash className="inline w-4 h-4 mr-1" />
                      Transaction ID
                    </label>
                    <p className="text-sm font-mono text-slate-800 break-all bg-white px-3 py-2 rounded-lg">
                      {selectedTransaction.transactionId ||
                        selectedTransaction.id}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <DollarSign className="inline w-4 h-4 mr-1" />
                      Amount
                    </label>
                    <p className="text-lg font-bold text-slate-800">
                      ${selectedTransaction.amount}
                    </p>
                  </div>
                  {selectedTransaction.actualBalance !== undefined &&
                    selectedTransaction.type === "deposit" &&
                    selectedTransaction.status === "confirmed" && (
                      <div className="bg-blue-50 rounded-xl p-4">
                        <label className="block text-sm font-semibold text-blue-700 mb-2">
                          Actual Amount Received
                        </label>
                        <p className="text-lg font-bold text-blue-800">
                          ${selectedTransaction.actualBalance}
                        </p>
                      </div>
                    )}
                  {selectedTransaction.netAmount !== undefined &&
                    selectedTransaction.status !== "expired" && (
                      <div className="bg-green-50 rounded-xl p-4">
                        <label className="block text-sm font-semibold text-green-700 mb-2">
                          Net Amount
                        </label>
                        <p className="text-lg font-bold text-green-800">
                          ${selectedTransaction.netAmount}
                        </p>
                      </div>
                    )}
                  {selectedTransaction.fee !== undefined &&
                    selectedTransaction.status !== "expired" && (
                      <div className="bg-red-50 rounded-xl p-4">
                        <label className="block text-sm font-semibold text-red-700 mb-2">
                          Fee
                        </label>
                        <p className="text-lg font-bold text-red-800">
                          ${selectedTransaction.fee}
                        </p>
                      </div>
                    )}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Currency Type
                    </label>
                    <p className="text-sm font-medium text-slate-800">
                      {selectedTransaction.currencyType}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <Wallet className="inline w-4 h-4 mr-1" />
                      Wallet Address
                    </label>
                    <p className="text-sm font-mono text-slate-800 break-all bg-white px-3 py-2 rounded-lg">
                      {selectedTransaction.walletAddress ||
                        selectedTransaction.clientWallet}
                    </p>
                  </div>
                  {selectedTransaction.marchantUserId && (
                    <div className="bg-slate-50 rounded-xl p-4">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        User ID
                      </label>
                      <p className="text-sm font-medium text-slate-800">
                        {selectedTransaction.marchantUserId || "N/A"}
                      </p>
                    </div>
                  )}
                </div>
                {/* Payment Status for Deposits */}
                {selectedTransaction.type === "deposit" &&
                  selectedTransaction.paymentStatus &&
                  selectedTransaction.status === "confirmed" && (
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 sm:p-6">
                      <label className="block text-sm font-semibold text-slate-700 mb-3">
                        Payment Status
                      </label>
                      <div className="text-center">
                        <span
                          className={`inline-block px-6 py-3 text-base font-bold rounded-xl ${
                            selectedTransaction.paymentStatus === "Overpaid"
                              ? "bg-green-100 text-green-800 border-2 border-green-300"
                              : selectedTransaction.paymentStatus ===
                                  "Underpaid"
                                ? "bg-red-100 text-red-800 border-2 border-red-300"
                                : "bg-blue-100 text-blue-800 border-2 border-blue-300"
                          }`}
                        >
                          {selectedTransaction.paymentStatus}
                        </span>
                      </div>
                    </div>
                  )}
                {/* Timestamps */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <Calendar className="inline w-4 h-4 mr-1" />
                      Created At
                    </label>
                    <p className="text-sm text-slate-800">
                      {new Date(selectedTransaction.createdAt).toLocaleString()}
                    </p>
                  </div>
                  {selectedTransaction.updatedAt && (
                    <div className="bg-slate-50 rounded-xl p-4">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        <Calendar className="inline w-4 h-4 mr-1" />
                        Updated At
                      </label>
                      <p className="text-sm text-slate-800">
                        {new Date(
                          selectedTransaction.updatedAt,
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {selectedTransaction.processedAt && (
                    <div className="bg-green-50 rounded-xl p-4">
                      <label className="block text-sm font-semibold text-green-700 mb-2">
                        <Calendar className="inline w-4 h-4 mr-1" />
                        Processed At
                      </label>
                      <p className="text-sm text-green-800">
                        {new Date(
                          selectedTransaction.processedAt,
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {selectedTransaction.confirmedAt && (
                    <div className="bg-blue-50 rounded-xl p-4">
                      <label className="block text-sm font-semibold text-blue-700 mb-2">
                        <Calendar className="inline w-4 h-4 mr-1" />
                        Confirmed At
                      </label>
                      <p className="text-sm text-blue-800">
                        {new Date(
                          selectedTransaction.confirmedAt,
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Transaction Hash */}
                {(selectedTransaction.transactionHash ||
                  selectedTransaction.blockchainTxHash ||
                  selectedTransaction.adminTransferData?.transactionHash) && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                      <Hash className="inline w-4 h-4 mr-1" />
                      Transaction Hash
                    </label>
                    {(() => {
                      const hash =
                        selectedTransaction.transactionHash ||
                        selectedTransaction.blockchainTxHash ||
                        selectedTransaction.adminTransferData?.transactionHash;
                      let explorerUrl = "";
                      if (selectedTransaction.currencyType === "USDT-TRC20") {
                        explorerUrl = `https://tronscan.org/#/transaction/${hash}`;
                      } else if (
                        selectedTransaction.currencyType === "USDT-ERC20"
                      ) {
                        explorerUrl = `https://etherscan.io/tx/${hash}`;
                      } else {
                        explorerUrl = "#";
                      }
                      return (
                        <div className="space-y-3">
                          <p className="text-sm font-mono text-slate-800 break-all bg-white px-3 py-2 rounded-lg border">
                            {hash}
                          </p>
                          <button
                            onClick={() => window.open(explorerUrl, "_blank")}
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
                          >
                            <ExternalLink size={16} />
                            <span>View on Explorer</span>
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {/* Admin Notes */}
                {selectedTransaction.adminNotes && (
                  <div className="bg-yellow-50 rounded-xl p-4 sm:p-6">
                    <label className="block text-sm font-semibold text-yellow-800 mb-3">
                      Admin Notes
                    </label>
                    <p className="text-sm text-yellow-800 bg-white px-4 py-3 rounded-lg border border-yellow-200">
                      {selectedTransaction.adminNotes}
                    </p>
                  </div>
                )}

                <div className="bg-blue-50 rounded-xl p-4 sm:p-6">
                  <label className="block text-sm font-semibold text-blue-800 mb-3">
                    Invoice ID
                  </label>
                  <p className="text-sm text-blue-800 bg-white px-4 py-3 rounded-lg border border-blue-200">
                    {selectedTransaction.invoiceNo}
                  </p>
                </div>
              </div>
            </div>
            {/* Modal Footer */}
            <div className="p-4 sm:p-6 border-t border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {selectedTransaction.publicToken && (
                    <>
                      <button
                        onClick={() => {
                          const baseUrl = `${API_BASE_URL}`.replace(/\/api\/v1$/, '');
                          window.open(`${baseUrl}/tx/${selectedTransaction.publicToken}`, '_blank');
                        }}
                        className="inline-flex items-center space-x-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800 transition-all duration-200"
                      >
                        <ExternalLink size={16} />
                        <span>View Public Receipt</span>
                      </button>
                      <button
                        onClick={() => {
                          const baseUrl = `${API_BASE_URL}`.replace(/\/api\/v1$/, '');
                          const url = `${baseUrl}/tx/${selectedTransaction.publicToken}`;
                          navigator.clipboard.writeText(url).then(() => {
                            setReceiptLinkCopied(true);
                            setTimeout(() => setReceiptLinkCopied(false), 2000);
                          });
                        }}
                        className="inline-flex items-center space-x-2 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50 transition-all duration-200"
                        title="Copy receipt link"
                      >
                        {receiptLinkCopied ? (
                          <>
                            <Check size={16} className="text-green-600" />
                            <span className="text-green-600">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy size={16} />
                            <span>Copy Link</span>
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
                <Button
                  onClick={() => setShowDetailsModal(false)}
                  variant="primary"
                >
                  Close Details
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllTransactions;
